import React, { useMemo, useState } from 'react'

import Button from 'components_new/atoms/Button'
import Skeleton from 'components_new/atoms/Skeleton'

import ContentBox from 'components_new/molecules/ContentBox'
import NewSettingsItem from 'components_new/molecules/NewSettingsItem'

import EditVariableAccountsDialog from './account_dialogs/EditVariableAccountsDialog'
import { EconomyAccountMap } from './account_dialogs/utils'

import { useFetchedKpiVariables } from 'redux/hooks/KpiVariables'
import { useIsManagementCompany } from 'redux/hooks/Customer'
import { useEconomySets } from 'redux/hooks/EconomySets'
import { useCustomKpis } from 'redux/hooks/Metrics'

import VariablesSection from './VariablesSection'
import VariablesAccountsSection from './AccountsSection'
import UnusedAccountsAlert from './UnusedAccountsAlert'
import { useKpiVariableOptionActions } from 'redux/actions/KpiVariableOptions'
import { useTranslation } from 'language/translator'

const EconomyAccountLevels = () => {
  const { tryPutKpiVariableOptions } = useKpiVariableOptionActions()
  const { t } = useTranslation()
  const isManagementCompany = useIsManagementCompany()
  const [economySets, loadingEconomySets] = useEconomySets()
  const [customKpis, loadingCustomKpis] = useCustomKpis()
  const [variables] = useFetchedKpiVariables()

  const [editVariable, setEditVariable] = useState<{
    economySetId: string
    variableId: string
    selectedAccounts: string[]
    limitToAccounts?: string[]
  } | null>(null)

  const levelZeroVariables = useMemo(
    () => variables.filter((v) => v.level === 0),
    [variables]
  )

  const accountsMap = useMemo(() => {
    const map: EconomyAccountMap = {}

    economySets.forEach((set) => {
      map[set.id] = {}

      set.accounts.forEach((acc) => {
        if (acc.name) {
          map[set.id][acc.number] = acc.name
        }
      })
    })

    return map
  }, [economySets])

  const loading = loadingEconomySets || loadingCustomKpis

  const [showLevels, setShowLevels] = useState<boolean>(false)
  const [showReferences, setShowReferences] = useState<boolean>(false)

  if (loading) {
    return <Skeleton variant="rounded" width="100%" height={98} />
  }

  return (
    <>
      <NewSettingsItem
        actions={
          <Button
            onClick={() => setShowLevels(!showLevels)}
            size="small"
            variant="text"
          >
            {`${showLevels ? t('global', 'hide') : t('global', 'show')}`}
          </Button>
        }
        helperText={t('accounting', 'account_levels_helper_text')}
        title={t('accounting', 'account_levels')}
      >
        {showLevels ? (
          <VariablesSection
            allVariables={variables}
            kpis={customKpis}
            level={0}
            parentVariable={null}
            variables={levelZeroVariables}
          />
        ) : null}
      </NewSettingsItem>

      <NewSettingsItem
        actions={
          <Button
            onClick={() => setShowReferences(!showReferences)}
            size="small"
            variant="text"
          >
            {`${showReferences ? t('global', 'hide') : t('global', 'show')}`}
          </Button>
        }
        helperText={t('accounting', 'account_references_helper_text')}
        title={t('accounting', 'account_references')}
      >
        {showReferences ? (
          <>
            {isManagementCompany || economySets.length > 1 ? (
              [...economySets]
                .sort((a, b) => Number(b.is_default) - Number(a.is_default))
                .map((economySet, i) => {
                  return (
                    <ContentBox
                      border={true}
                      gutterBottom={i !== economySets.length - 1}
                      key={economySet.id}
                      collapse={{
                        active: true,
                        defaultCollapsed: i !== 0
                      }}
                      header={{ title: economySet.name }}
                      size="small"
                    >
                      <UnusedAccountsAlert economySet={economySet} />
                      <VariablesAccountsSection
                        accountsMap={accountsMap}
                        allVariables={variables}
                        economySetId={economySet.id}
                        editVariable={editVariable}
                        level={0}
                        setEditVariable={setEditVariable}
                        variables={levelZeroVariables}
                      />
                    </ContentBox>
                  )
                })
            ) : (
              <>
                <UnusedAccountsAlert economySet={economySets[0]} />
                <VariablesAccountsSection
                  accountsMap={accountsMap}
                  allVariables={variables}
                  economySetId={economySets[0].id}
                  editVariable={editVariable}
                  level={0}
                  setEditVariable={setEditVariable}
                  variables={levelZeroVariables}
                />
              </>
            )}
          </>
        ) : null}
      </NewSettingsItem>

      {/* ----- Dialogs ----- */}
      {editVariable ? (
        <EditVariableAccountsDialog
          accountsMap={accountsMap}
          economySetId={editVariable.economySetId as string}
          limitToAccounts={editVariable.limitToAccounts}
          open={Boolean(editVariable)}
          onClose={() => setEditVariable(null)}
          onSave={(accounts, callback) => {
            tryPutKpiVariableOptions(
              editVariable.economySetId,
              editVariable.variableId,
              accounts,
              (err) => {
                if (!err) {
                  setEditVariable(null)
                }
                callback()
              }
            )
          }}
          selectedAccounts={editVariable.selectedAccounts}
        />
      ) : null}
    </>
  )
}

export default EconomyAccountLevels
